import BaseController from "niteroi/packages/system/controllers/Base.controller";
import JSONModel from "sap/ui/model/json/JSONModel";
import Table from "sap/m/Table";
import ColumnListItem from "sap/m/ColumnListItem";
import MessageBox from "sap/m/MessageBox";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {ILoadDataResponse, ISession} from "niteroi/packages/system/models/interfaces";

/**
 * Sessions Controller
 * Manages the admin sessions view: lists all active sessions grouped by company,
 * supports multi-selection for bulk deactivation, and navigates to user profiles.
 *
 * @namespace niteroi.packages.system.controllers
 */
export default class Sessions extends BaseController {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const route = router.getRoute("system/sessions");
        route.detachPatternMatched(this.onPatternMatchedHandler);
        route.attachPatternMatched(this.onPatternMatchedHandler);
    }

    protected onPatternMatched(): void {
        this.getData();
    }

    protected getData(): void {
        const model = this.createRequestModel();
        const data = "list=true";
        model.loadData(
            "/api/sessions",
            data,
            true,
            "GET",
            false,
            true,
            this.getRequestHeaders()
        ).then(() => {
            const raw = JSON.parse(model.getJSON()) as { sessions?: ISession[] };
            const sessions: ISession[] = raw.sessions ?? [];
            const sessionsModel = new JSONModel({ items: sessions, selectedCount: 0 });
            sessionsModel.setSizeLimit(9999);
            this.setModel(sessionsModel, "sessions");
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        });
    }

    public onExit(): void {
        const model = this.getModel("sessions");
        if (model) {
            model.destroy();
        }
    }

    public onSessionSelectionChange(): void {
        const table = this.byId("sessionsTable") as Table;
        const selectedItems = table.getSelectedItems();
        const sessionsModel = this.getModel("sessions");
        sessionsModel.setProperty("/selectedCount", selectedItems.length);
    }

    public onPressButtonDeactivateSessions(): void {
        const table = this.byId("sessionsTable") as Table;
        const selectedItems = table.getSelectedItems();
        if (selectedItems.length === 0) { return; }

        MessageBox.confirm(this.getText("sessionsConfirmDeactivate"), {
            onClose: (action: string) => {
                if (action !== MessageBox.Action.OK) { return; }

                const deactivations = selectedItems.map((item) => {
                    const ctx = (item as ColumnListItem).getBindingContext("sessions");
                    const session = ctx?.getModel().getProperty(ctx.getPath()) as ISession;
                    if (!session?.sessionId) { return Promise.resolve(); }

                    const requestModel = this.createRequestModel();
                    return requestModel.loadData(
                        `/api/sessions/${session.sessionId}`,
                        "",
                        true,
                        "DELETE",
                        false,
                        true,
                        this.getRequestHeaders()
                    );
                });

                Promise.all(deactivations).then(() => {
                    this.showToast(this.getText("sessionsDeactivateSuccess"));
                    this.getData();
                }).catch((response: ILoadDataResponse) => {
                    const responseText = JSON.parse(response.responseText);
                    this.showToast(`${this.getText("sessionsDeactivateError")}: ${responseText.code} - ${responseText.error}`);
                });
            }
        });
    }

    public onPressButtonViewUserProfile(): void {
        const table = this.byId("sessionsTable") as Table;
        const selectedItems = table.getSelectedItems();
        if (selectedItems.length !== 1) { return; }

        const ctx = (selectedItems[0] as ColumnListItem).getBindingContext("sessions");
        const session = ctx?.getModel().getProperty(ctx.getPath()) as ISession;
        if (!session?.userId) { return; }

        this.navTo("system/users/edit", { id: session.userId }, false);
    }

    public onSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "sessionsTable", ["companyName", "userEmail", "ipAddress", "locale"]);
    }
}
